import { useQuery } from "@tanstack/react-query";

import { currentClaims } from "../lib/auth";
import {
  Godown,
  PortalUser,
  getState,
  listDistricts,
  listGodowns,
  listInvitations,
  listReports,
  listUsers,
  me,
} from "../lib/api";

/** The state this state-head manages, from their JWT scope. */
export function stateScopeId(): number | null {
  const c = currentClaims();
  const s = c?.scopes?.find((x) => x.scope_type === "state");
  return s ? s.scope_id : null;
}

export function useSHState() {
  const id = stateScopeId();
  return useQuery({
    queryKey: ["sh-state", id],
    queryFn: () => getState(id!),
    enabled: id != null,
  });
}

export function useSHDistricts() {
  const id = stateScopeId();
  return useQuery({
    queryKey: ["sh-districts", id],
    queryFn: () => listDistricts({ state_id: id! }),
    enabled: id != null,
  });
}

export function useSHGodowns() {
  return useQuery({ queryKey: ["sh-godowns"], queryFn: listGodowns });
}

export function useSHReports() {
  return useQuery({ queryKey: ["sh-reports"], queryFn: listReports });
}

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: me, staleTime: Infinity });
}

export function useSHInvitations() {
  return useQuery({ queryKey: ["sh-invitations"], queryFn: listInvitations });
}

/** Everyone in the tenant, so we can put a real name against an email.
 * Scope-filtered by the backend to the caller's enterprise. */
export function useSHUsers() {
  return useQuery({ queryKey: ["sh-users"], queryFn: listUsers });
}

/** The confirmed district head for a district: the user whose role is
 * district_head and who holds a district scope for this district.
 * Returns null when nobody has accepted yet. */
export function districtHeadUser(
  users: PortalUser[],
  districtId: number,
): PortalUser | null {
  return (
    users.find(
      (u) =>
        u.role === "district_head" &&
        (u.scopes ?? []).some(
          (s) => s.scope_type === "district" && s.scope_id === districtId,
        ),
    ) ?? null
  );
}

/** "Firstname Lastname" from a user, trimmed; null when both blank. */
export function fullName(u: {
  first_name?: string | null;
  last_name?: string | null;
}): string | null {
  const n = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return n || null;
}

/** The commodity chip for the top bar, derived from the grains the
 * state's godowns are configured to grade. One grain → its name; more
 * than one → "N GRAINS"; none → null (no chip). */
export function commodityLabel(godowns: Godown[]): string | null {
  const grains = new Set<string>();
  for (const g of godowns) {
    if (g.grain_configs && g.grain_configs.length) {
      g.grain_configs.forEach((c) => grains.add(c.grain_type));
    } else if (g.grain_type) {
      grains.add(g.grain_type);
    }
  }
  if (grains.size === 0) return null;
  if (grains.size === 1) return [...grains][0].toUpperCase();
  return `${grains.size} GRAINS`;
}

/** "01 Aug" style date for the activity feed. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

/** Turn a role slug into words for feed copy (kept plain, no slugs). */
export function roleWords(role: string): string {
  return (role || "").replace(/_/g, " ");
}

/** A report counts as "passed" when its verdict reads pass/accept. */
export function isPass(verdict: string | null | undefined): boolean {
  const v = (verdict || "").toLowerCase();
  return v === "pass" || v === "accept" || v === "accepted";
}

/** en-IN grouping to match the mock's number style. */
export const fmtInt = (n: number): string => n.toLocaleString("en-IN");

/** Placeholder for a figure we don't track yet. */
export const DASH = "—";
